import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles } from "lucide-react";

const PROCESSES = ["Revenue", "Purchase", "Inventory", "Payroll", "Treasury", "Fixed Assets", "Financial Close", "Material / Significant Transactions"];
const DIRECTIONS = ["Source→GL", "GL→Source"];
const SELECTION = ["Material amount", "Unusual transaction", "Related party", "Year-end", "New process", "New vendor/customer", "Management override", "Complex transaction"];

export default function Walkthrough({ id }) {
  const [processes, setProcesses] = useState([]);
  const [active, setActive] = useState(null);
  const [newProc, setNewProc] = useState("Revenue");

  const load = async () => {
    const { data } = await api.get(`/engagements/${id}/processes`);
    setProcesses(data);
    if (data.length && !active) setActive(data[0]);
    else if (active) setActive(data.find((p) => p.id === active.id) || data[0] || null);
  };
  useEffect(() => { load(); }, [id]);

  const create = async () => {
    const { data } = await api.post(`/engagements/${id}/processes`, { name: newProc });
    toast.success("Proses dibuat"); setActive(data); load();
  };
  const saveProc = async (fields) => { try { await api.put(`/engagements/${id}/processes/${active.id}`, fields); toast.success("Disimpan"); load(); } catch(e) { toast.error(String(e.response?.data?.detail || "Gagal")); } };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
      <div className="bg-white border border-border rounded-sm p-3 h-fit">
        <div className="font-head font-semibold text-sm mb-2">Proses Bisnis</div>
        <div className="space-y-1 mb-3">
          {processes.map((p) => (
            <button key={p.id} onClick={() => setActive(p)} data-testid={`wt-proc-${p.id}`}
              className={`block w-full text-left text-sm px-2 py-1.5 rounded-sm ${active?.id === p.id ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}>{p.name}</button>
          ))}
        </div>
        <div className="flex gap-1">
          <Select value={newProc} onValueChange={setNewProc}><SelectTrigger className="h-8 text-xs" data-testid="wt-new-select"><SelectValue /></SelectTrigger>
            <SelectContent>{PROCESSES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select>
          <Button size="sm" className="h-8 px-2" onClick={create} data-testid="wt-add-proc"><Plus size={14} /></Button>
        </div>
      </div>
      <div className="lg:col-span-3 space-y-4 min-w-0">
        {!active ? <div className="text-sm text-muted-foreground">Buat/pilih proses untuk memulai walkthrough.</div> : (
          <ProcessDetail key={active.id} id={id} proc={active} onSave={saveProc} reload={load} />
        )}
      </div>
    </div>
  );
}

function ProcessDetail({ id, proc, onSave, reload }) {
  const [narrative, setNarrative] = useState(proc.narrative || "");
  const [conclusion, setConclusion] = useState(proc.conclusion || "");
  const [owner, setOwner] = useState(proc.owner || "");
  const [selectionReason, setSelectionReason] = useState(proc.selection_reason || "");
  const [reason, setReason] = useState("");
  const locked = ['Prepared','Reviewed','Stale'].includes(proc.status);
  const dirty = narrative !== (proc.narrative || "") || conclusion !== (proc.conclusion || "") || owner !== (proc.owner || "") || selectionReason !== (proc.selection_reason || "");
  const action = async (action) => {
    if ((dirty || controlEdit) && action !== 'reopen') return toast.error("Simpan perubahan sebelum tanda tangan");
    if (!reason.trim()) return toast.error("Alasan wajib diisi");
    try { await api.post(`/engagements/${id}/processes/${proc.id}/action`, { action, expected_version: proc.version || 0, reason }); setReason(""); await reload(); }
    catch(e) { toast.error(String(e.response?.data?.detail || "Gagal")); }
  };
  const [evidence, setEvidence] = useState([]);
  useEffect(() => { let active=true; api.get(`/engagements/${id}/evidence`).then(r=>{if(active)setEvidence(r.data);}).catch(()=>toast.error("Bukti gagal dimuat")); return ()=>{active=false;}; }, [id]);
  const [controlEdit, setControlEdit] = useState(null);
  const [risks, setRisks] = useState([]);
  useEffect(()=>{api.get(`/engagements/${id}/risks`).then(r=>setRisks(r.data)).catch(()=>toast.error("Risiko gagal dimuat"));},[id]);
  const [aiLoading, setAiLoading] = useState(false);
  const [step, setStep] = useState({ direction: "Source→GL", stage: "", document: "", document_no: "", amount: "", assertion: "", accounting_entry: "", observation: "", result: "Belum diuji", evidence_ids: [], exception: "" });
  const [ctrl, setCtrl] = useState({ description: "", assertion: "", frequency: "Harian", nature: "Manual", type: "Preventive", design: "Belum dinilai", implementation: "Belum dinilai" });

  const aiDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/engagements/${id}/ai/status_summary`);
      const s = data.result?.suggestions?.map((x) => `${x.title}: ${x.detail}`).join("\n") || "";
      setNarrative((prev) => (prev ? prev + "\n\n" : "") + "[AI Draft — Auditor Validation Required]\n" + s);
      toast.info("Draf AI ditambahkan — validasi auditor diperlukan");
    } catch { toast.error("AI gagal"); } finally { setAiLoading(false); }
  };
  const addStep = async () => {
    if (!step.document) return toast.error("Dokumen wajib diisi");
    try { await api.post(`/engagements/${id}/processes/${proc.id}/steps`, step);
    toast.success("Langkah walkthrough ditambahkan"); setStep({ ...step, document: "", document_no: "", amount: "", result:"Belum diuji", evidence_ids:[], observation:"", exception:"" }); reload(); } catch(e) { toast.error(String(e.response?.data?.detail || "Gagal")); }
  };
  const delStep = async (sid) => { await api.delete(`/engagements/${id}/processes/${proc.id}/steps/${sid}`); reload(); };
  const saveControl = async () => {
    const fields = ['description','owner','assertion','risk','risk_id','design','implementation','assessment_rationale','deficiency'];
    try { await api.put(`/engagements/${id}/processes/${proc.id}/controls/${controlEdit.id}`, Object.fromEntries(fields.map(k=>[k,controlEdit[k] || '']))); setControlEdit(null); await reload(); }
    catch(e) { toast.error(String(e.response?.data?.detail || 'Gagal')); }
  };
  const addCtrl = async () => {
    if (!ctrl.description) return toast.error("Deskripsi kontrol wajib");
    await api.post(`/engagements/${id}/processes/${proc.id}/controls`, ctrl);
    toast.success("Kontrol ditambahkan"); setCtrl({ ...ctrl, description: "" }); reload();
  };

  return (
    <>
      <div className="bg-white border p-4 space-y-2">
        <div>Status: {proc.status || 'Draft'}</div>
        <div>Preparer: {proc.prepared_by_user_id || 'Pending'} · Reviewer: {proc.reviewed_by_user_id || 'Pending'}</div>
        {(proc.blockers || []).map((b,i)=><div key={i} className="text-sm text-amber-700">{b}</div>)}
        <Textarea aria-label="Alasan tanda tangan walkthrough" value={reason} onChange={e=>setReason(e.target.value)} />
        {!locked && <Button disabled={dirty} onClick={()=>action('prepare')}>Prepare</Button>}
        {proc.status === 'Prepared' && <Button onClick={()=>action('review')}>Review</Button>}
        {locked && <Button onClick={()=>action('reopen')}>Reopen</Button>}
      </div>
      <fieldset disabled={locked} className="space-y-4 min-w-0">
      <div className="bg-white border border-border rounded-sm p-4 space-y-2">
        <div className="flex items-center justify-between">
          <div className="font-head font-semibold">{proc.name} — Naratif Proses</div>
          <Button size="sm" variant="outline" onClick={aiDraft} disabled={aiLoading} data-testid="wt-ai-draft"><Sparkles size={14} className="mr-1" />{aiLoading ? "..." : "Draf AI"}</Button>
        </div>
        <div className="text-[11px] text-indigo-600">AI dapat menyusun draf; wajib berlabel "AI Draft — Auditor Validation Required".</div>
        <Textarea rows={4} value={narrative} onChange={(e) => setNarrative(e.target.value)} data-testid="wt-narrative" />
        <div className="text-[11px] text-muted-foreground">Basis pemilihan transaksi: {SELECTION.join(", ")}. Walkthrough BUKAN sampling audit.</div>
        <Input aria-label="Pemilik proses" value={owner} onChange={e=>setOwner(e.target.value)} placeholder="Pemilik proses" />
        <Textarea aria-label="Alasan pemilihan transaksi" value={selectionReason} onChange={e=>setSelectionReason(e.target.value)} placeholder="Alasan pemilihan transaksi aktual" />
        <Button size="sm" onClick={() => onSave({ narrative, owner, selection_reason: selectionReason })} data-testid="wt-save-narrative">Simpan Naratif</Button>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 space-y-3">
        <div className="font-head font-semibold text-sm">Kontrol (Desain & Implementasi)</div>
        <div className="border border-border rounded-sm divide-y">
          {(proc.controls || []).map((c) => (
            <div key={c.id} className="p-2 text-sm flex justify-between" data-testid={`wt-ctrl-${c.id}`}>
              <span>{c.description} <span className="text-[10px] px-1 border rounded-sm bg-muted ml-1">{c.nature}</span> <span className="text-[10px] px-1 border rounded-sm bg-muted">{c.type}</span></span>
              <span className="text-xs text-muted-foreground">{c.design} / {c.implementation}</span><Button size="sm" onClick={()=>setControlEdit({...c})}>Nilai kontrol</Button>
            </div>
          ))}
          {(proc.controls || []).length === 0 && <div className="p-2 text-sm text-muted-foreground">Belum ada kontrol.</div>}
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Input className="col-span-2 h-8" placeholder="Deskripsi kontrol" value={ctrl.description} onChange={(e) => setCtrl({ ...ctrl, description: e.target.value })} data-testid="wt-ctrl-desc" />
          <Input className="h-8" placeholder="Asersi" value={ctrl.assertion} onChange={(e) => setCtrl({ ...ctrl, assertion: e.target.value })} />
          <Select value={ctrl.nature} onValueChange={(v) => setCtrl({ ...ctrl, nature: v })}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{["Manual", "Automated", "IT-dependent manual"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        </div>
        {controlEdit && <div className="border p-3 space-y-2">
          {['description','owner','assertion','risk','design','implementation','assessment_rationale','deficiency'].map(k=><Label key={k}>{k}<Textarea value={controlEdit[k] || ''} onChange={e=>setControlEdit({...controlEdit,[k]:e.target.value})} /></Label>)}
          <Label>Risiko terkait<select value={controlEdit.risk_id || ''} onChange={e=>setControlEdit({...controlEdit,risk_id:e.target.value})}><option value="">Pilih risiko</option>{risks.map(r=><option key={r.id} value={r.id}>{r.description || r.title || r.id}</option>)}</select></Label>
          <Button onClick={saveControl}>Simpan penilaian kontrol</Button><Button variant="outline" onClick={()=>setControlEdit(null)}>Batal</Button>
        </div>}
        <Button size="sm" variant="outline" onClick={addCtrl} data-testid="wt-add-ctrl"><Plus size={14} className="mr-1" />Tambah Kontrol</Button>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 space-y-3">
        <div className="font-head font-semibold text-sm">Langkah Walkthrough (Source ⇄ GL)</div>
        <div className="overflow-x-auto border border-border rounded-sm">
          <table className="w-full audit-table">
            <thead><tr><th>#</th><th>Arah</th><th>Tahap</th><th>Dokumen</th><th>No</th><th>Nilai</th><th>Asersi</th><th>Jurnal</th><th>Hasil</th><th></th></tr></thead>
            <tbody>
              {(proc.steps || []).map((s) => (
                <tr key={s.id} data-testid={`wt-step-${s.id}`}>
                  <td>{s.step_number}</td>
                  <td><span className="text-[10px] px-1 border rounded-sm bg-muted">{s.direction}</span></td>
                  <td>{s.stage}</td><td className="font-medium">{s.document}</td><td>{s.document_no}</td>
                  <td className="tabular text-right">{s.amount}</td><td className="text-xs">{s.assertion}</td><td className="text-xs">{s.accounting_entry}</td>
                  <td><span className={s.result === "Sesuai" ? "text-green-700" : "text-red-600"}>{s.result}</span></td>
                  <td><Button variant="ghost" size="sm" className="h-6 px-1 text-destructive" onClick={() => delStep(s.id)}><Trash2 size={12} /></Button></td>
                </tr>
              ))}
              {(proc.steps || []).length === 0 && <tr><td colSpan={10} className="text-center text-muted-foreground py-4">Belum ada langkah.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2">
          <Select value={step.direction} onValueChange={(v) => setStep({ ...step, direction: v })}><SelectTrigger className="h-8 text-xs" data-testid="wt-step-dir"><SelectValue /></SelectTrigger><SelectContent>{DIRECTIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent></Select>
          <Input className="h-8" placeholder="Tahap" value={step.stage} onChange={(e) => setStep({ ...step, stage: e.target.value })} />
          <Input className="h-8" placeholder="Dokumen" value={step.document} onChange={(e) => setStep({ ...step, document: e.target.value })} data-testid="wt-step-doc" />
          <Input className="h-8" placeholder="No" value={step.document_no} onChange={(e) => setStep({ ...step, document_no: e.target.value })} />
          <Input className="h-8" placeholder="Nilai" value={step.amount} onChange={(e) => setStep({ ...step, amount: e.target.value })} />
          <Select value={step.result} onValueChange={(v) => setStep({ ...step, result: v })}><SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger><SelectContent>{["Belum diuji", "Sesuai", "Eksepsi"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select>
        </div>
        <Textarea placeholder="Observasi atas pengujian aktual" value={step.observation} onChange={e=>setStep({...step,observation:e.target.value})} />
        <Textarea placeholder="Eksepsi (wajib jika hasil Eksepsi)" value={step.exception} onChange={e=>setStep({...step,exception:e.target.value})} />
        <Label>Bukti aktual</Label><select className="border p-2 w-full" value={step.evidence_ids[0] || ""} onChange={e=>setStep({...step,evidence_ids:e.target.value?[e.target.value]:[]})}><option value="">Pilih bukti</option>{evidence.map(ev=><option key={ev.id} value={ev.id}>{ev.file_name || ev.filename || ev.id}</option>)}</select>
        <Button size="sm" onClick={addStep} data-testid="wt-add-step"><Plus size={14} className="mr-1" />Tambah Langkah</Button>
      </div>

      <div className="bg-white border border-border rounded-sm p-4 space-y-2">
        <div className="font-head font-semibold text-sm">Kesimpulan</div>
        <Textarea rows={2} value={conclusion} onChange={(e) => setConclusion(e.target.value)} data-testid="wt-conclusion" />
        <Button size="sm" onClick={() => onSave({ conclusion })}>Simpan Kesimpulan</Button>
      </div>
      </fieldset>
    </>
  );
}
